import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Heart, Briefcase, Calendar, Handshake, Users, Trash2, Share2, Edit2 } from 'lucide-react';

/**
 * Thumbnail component with loading state
 */
const Thumbnail = ({ src, alt, className }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className={`relative ${className}`}>
      {!loaded && !error && (
        <div className="absolute inset-0 bg-surface-sunken animate-pulse rounded-lg" />
      )}
      {error ? (
        <div className="w-full h-full bg-surface-muted rounded-lg flex items-center justify-center">
          <Briefcase size={24} className="text-ink-subtle" />
        </div>
      ) : (
        <img
          src={src}
          alt={alt}
          className={`w-full h-full rounded-lg object-cover transition-opacity duration-300 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setLoaded(true)}
          onError={() => setError(true)}
        />
      )}
    </div>
  );
};

/**
 * FavoritesView Component
 * Displays user's favorites lists
 */
const FavoritesView = ({ navigate, chatId, safeFetch }) => {
  const [favoritesLists, setFavoritesLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFavoritesList, setSelectedFavoritesList] = useState(null);
  const [expandedFavoritesData, setExpandedFavoritesData] = useState(null);
  const [loadingFavoritesData, setLoadingFavoritesData] = useState(false);
  const [editingFavoritesList, setEditingFavoritesList] = useState(null);
  const [editListName, setEditListName] = useState('');
  const [sharingList, setSharingList] = useState(null);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);

  // Fetch favorites lists
  const fetchFavoritesLists = async () => {
    if (!chatId || chatId === 'guest') {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const res = await safeFetch(
        `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/user-favorites-lists?chat_id=${chatId}`,
        chatId
      );
      const data = await res.json();
      const parsedLists = (data || []).map(list => ({
        ...list,
        data: typeof list.data === 'string' ? JSON.parse(list.data) : list.data
      }));
      setFavoritesLists(parsedLists);
    } catch (err) {
      console.error('Failed to fetch favorites lists:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFavoritesLists();
  }, [chatId]);

  // Fetch detailed data for a favorites list
  const fetchFavoritesListData = async (list) => {
    setLoadingFavoritesData(true);
    try {
      const businesses = await Promise.all(
        (list.data?.businesses || []).map(async (businessId) => {
          const res = await safeFetch(
            `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/business-info?business_id=${businessId}&chat_id=${chatId}`,
            chatId
          );
          const data = await res.json();
          console.log('Business data received:', data);
          return data;
        })
      );

      const events = await Promise.all(
        (list.data?.events || []).map(async (businessId) => {
          const res = await safeFetch(
            `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/business-info?business_id=${businessId}&chat_id=${chatId}`,
            chatId
          );
          const data = await res.json();
          console.log('Event data received:', data);
          return data;
        })
      );

      const barters = await Promise.all(
        (list.data?.barters || []).map(async (businessId) => {
          const res = await safeFetch(
            `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/business-info?business_id=${businessId}&chat_id=${chatId}`,
            chatId
          );
          const data = await res.json();
          console.log('Barter data received:', data);
          return data;
        })
      );

      console.log('All fetched data:', { businesses, events, barters });

      setExpandedFavoritesData({
        businesses,
        events,
        barters,
      });
    } catch (err) {
      console.error('Failed to fetch favorites list data:', err);
    } finally {
      setLoadingFavoritesData(false);
    }
  };

  // Handle delete favorites list
  const handleDeleteFavoritesList = async (playlistId) => {
    if (!confirm('האם אתה בטוח שברצונך למחוק את הרשימה?')) return;

    try {
      await safeFetch(
        `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/delete-favorites-list`,
        chatId,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ playlist_id: playlistId, chat_id: chatId })
        }
      );
      fetchFavoritesLists();
    } catch (err) {
      console.error('Failed to delete favorites list:', err);
      alert('שגיאה במחיקת הרשימה');
    }
  };

  // Handle edit favorites list
  const handleEditFavoritesList = (list) => {
    setEditingFavoritesList(list);
    setEditListName(list.data?.name || '');
  };

  const handleSaveEditedListName = async () => {
    if (!editListName.trim()) {
      alert('נא להזין שם לרשימה');
      return;
    }

    try {
      const updatedData = {
        ...editingFavoritesList.data,
        name: editListName.trim()
      };

      await safeFetch(
        `https://j1gefbe2q0.execute-api.us-west-2.amazonaws.com/default/update-favorites-list`,
        chatId,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            playlist_id: editingFavoritesList.playlist_id,
            chat_id: chatId,
            data: updatedData
          })
        }
      );

      setEditingFavoritesList(null);
      setEditListName('');
      fetchFavoritesLists();
    } catch (err) {
      console.error('Failed to update favorites list name:', err);
      alert('שגיאה בעדכון שם הרשימה');
    }
  };

  // Handle share favorites list
  const handleShareFavoritesList = (list) => {
    setSharingList(list);
    setShareLinkCopied(false);
  };

  const copyShareLink = () => {
    const shareUrl = `${window.location.origin}?view=favorites-list&list_id=${sharingList.playlist_id}`;
    navigator.clipboard.writeText(shareUrl);
    setShareLinkCopied(true);
    setTimeout(() => setShareLinkCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-surface-muted via-white to-info-50/20 flex items-center justify-center" style={{ direction: 'rtl' }}>
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-danger-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-ink-muted">טוען רשימות מועדפים...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-surface-muted via-white to-info-50/20 pb-24" style={{ direction: 'rtl' }}>
      {/* Hero Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-secondary-400 via-secondary-400 to-danger-500 pt-16 md:pt-20 pb-6 px-4">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(255,255,255,0.15),transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_70%,rgba(255,255,255,0.1),transparent_50%)]" />

        <div className="container max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-14 h-14 mx-auto mb-3 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center shadow-lg">
              <Heart size={28} className="text-white fill-white" strokeWidth={2} />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-white mb-2">
              המועדפים שלי
            </h1>
            <p className="text-sm md:text-base text-white/90 max-w-2xl mx-auto">
              כל הרשימות והמועדפים שלך במקום אחד
            </p>
          </motion.div>
        </div>
      </div>

      {/* Content */}
      <div className="container max-w-6xl mx-auto px-4 py-8">
        {chatId === 'guest' ? (
          <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle">
            <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-danger-50 to-danger-100/50 flex items-center justify-center">
              <Heart size={40} className="text-danger-500" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-bold text-ink-strong mb-2">נדרשת התחברות</h3>
            <p className="text-sm text-ink-muted mb-6">כדי לראות ולנהל רשימות מועדפים, עליך להתחבר למערכת</p>
            <button
              onClick={() => {
                localStorage.removeItem('chat_id');
                window.location.href = '/';
              }}
              className="px-6 py-3 rounded-2xl bg-gradient-to-br from-danger-500 to-danger-600 text-white hover:from-danger-600 hover:to-danger-700 transition-all active:scale-95 font-bold shadow-lg"
            >
              התחבר עכשיו
            </button>
          </div>
        ) : favoritesLists.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center py-20 bg-white rounded-2xl shadow-sm border border-line-subtle"
          >
            <div className="w-24 h-24 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-danger-50 to-danger-100/50 flex items-center justify-center">
              <Heart size={40} className="text-danger-500" strokeWidth={1.5} />
            </div>
            <h3 className="text-xl font-bold text-ink-strong mb-2">אין רשימות מועדפים עדיין</h3>
            <p className="text-sm text-ink-muted mb-6">התחל להוסיף עסקים ומפגשים לרשימות המועדפים שלך</p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            {favoritesLists.map(list => {
              const businessCount = list.data?.businesses?.length || 0;
              const eventCount = list.data?.events?.length || 0;
              const barterCount = list.data?.barters?.length || 0;
              const groupCount = list.data?.groups?.length || 0;
              const totalCount = businessCount + eventCount + barterCount + groupCount;

              return (
                <div
                  key={list.playlist_id}
                  className="group relative bg-white rounded-2xl p-6 shadow-sm border border-line-subtle hover:shadow-lg transition-all duration-300 cursor-pointer hover:-translate-y-1"
                  onClick={() => {
                    setSelectedFavoritesList(list);
                    fetchFavoritesListData(list);
                  }}
                >
                  <div className="absolute top-3 left-3 flex gap-2 z-10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleShareFavoritesList(list);
                      }}
                      className="bg-white/90 text-secondary-500 rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-md hover:bg-secondary-500 hover:text-white transition-all transform hover:scale-110 border border-secondary-500/20"
                      title="שתף רשימה"
                    >
                      <Share2 size={16} strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditFavoritesList(list);
                      }}
                      className="bg-white/90 text-info-500 rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-md hover:bg-info-500 hover:text-white transition-all transform hover:scale-110 border border-info-100"
                      title="שנה שם רשימה"
                    >
                      <Edit2 size={16} strokeWidth={2.5} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteFavoritesList(list.playlist_id);
                      }}
                      className="bg-white/90 text-ink-subtle rounded-full p-2 w-8 h-8 flex items-center justify-center shadow-md hover:bg-surface-muted hover:text-danger-500 transition-all transform hover:scale-110 border border-line"
                      title="מחק רשימה"
                    >
                      <Trash2 size={16} strokeWidth={2.5} />
                    </button>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-danger-500 to-danger-600 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                      <Heart size={28} className="text-white fill-white" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-lg font-bold text-ink-strong mb-2 truncate">
                        {list.data?.name || 'ללא שם'}
                      </h3>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {businessCount > 0 && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-secondary-500/10 text-secondary-700 text-xs font-semibold rounded-full border border-secondary-500/20">
                            <Briefcase size={12} />
                            {businessCount} עסקים
                          </span>
                        )}
                        {eventCount > 0 && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-info-50 text-info-700 text-xs font-semibold rounded-full border border-info-100">
                            <Calendar size={12} />
                            {eventCount} מפגשים
                          </span>
                        )}
                        {barterCount > 0 && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-success-50 text-success-700 text-xs font-semibold rounded-full border border-success-100">
                            <Handshake size={12} />
                            {barterCount} ברטרים
                          </span>
                        )}
                        {groupCount > 0 && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 bg-brand-50 text-brand-700 text-xs font-semibold rounded-full border border-brand-200">
                            <Users size={12} />
                            {groupCount} קבוצות
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-ink-subtle">
                        סה"כ {totalCount} פריטים
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}
      </div>

      {/* Edit List Name Modal */}
      {editingFavoritesList && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setEditingFavoritesList(null)}>
          <div className="bg-white rounded-3xl shadow-elevated p-6 w-96 mx-4 animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-ink-strong mb-4 text-center font-heading font-bold text-xl">שנה שם רשימה</h3>
            <input
              type="text"
              value={editListName}
              onChange={(e) => setEditListName(e.target.value)}
              placeholder="שם הרשימה"
              className="w-full px-4 py-3 rounded-xl border border-line-strong focus:border-danger-500 focus:outline-none mb-4"
              autoFocus
            />
            <div className="flex flex-col gap-3">
              <button
                onClick={handleSaveEditedListName}
                className="px-6 py-3 rounded-2xl bg-gradient-to-br from-danger-500 to-danger-600 text-white hover:from-danger-600 hover:to-danger-700 transition-all active:scale-95 font-bold shadow-lg"
              >
                שמור
              </button>
              <button
                onClick={() => setEditingFavoritesList(null)}
                className="px-6 py-2.5 rounded-2xl bg-surface-muted hover:bg-surface-sunken text-ink font-bold transition-all"
              >
                ביטול
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share List Modal */}
      {sharingList && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setSharingList(null)}>
          <div className="bg-white rounded-3xl shadow-elevated p-6 w-96 mx-4 animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-ink-strong mb-4 text-center font-heading font-bold text-xl">שתף רשימה</h3>
            <p className="text-ink-muted mb-4 text-center">העתק את הקישור ושתף עם חברים</p>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={`${window.location.origin}?view=favorites-list&list_id=${sharingList.playlist_id}`}
                readOnly
                className="flex-1 px-4 py-3 rounded-xl border border-line-strong bg-surface-muted text-sm"
              />
              <button
                onClick={copyShareLink}
                className="px-4 py-3 rounded-xl bg-gradient-to-br from-danger-500 to-danger-600 text-white hover:from-danger-600 hover:to-danger-700 transition-all active:scale-95 font-bold shadow-lg flex items-center gap-2"
              >
                {shareLinkCopied ? <span>✓</span> : <span>העתק</span>}
              </button>
            </div>
            <button
              onClick={() => setSharingList(null)}
              className="w-full px-6 py-2.5 rounded-2xl bg-surface-muted hover:bg-surface-sunken text-ink font-bold transition-all"
            >
              סגור
            </button>
          </div>
        </div>
      )}

      {/* Expanded List Details Modal */}
      {selectedFavoritesList && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setSelectedFavoritesList(null)}>
          <div className="bg-white rounded-3xl shadow-elevated p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto animate-scale-in" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-2xl font-bold text-ink-strong">{selectedFavoritesList.data?.name || 'ללא שם'}</h3>
              <button
                onClick={() => setSelectedFavoritesList(null)}
                className="text-ink-subtle hover:text-ink text-2xl"
              >
                ×
              </button>
            </div>

            {loadingFavoritesData ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 border-4 border-danger-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-ink-muted">טוען פרטים...</p>
              </div>
            ) : expandedFavoritesData ? (
              <div className="space-y-6">
                {expandedFavoritesData.businesses?.length > 0 && (
                  <div>
                    <h4 className="text-lg font-bold text-ink-strong mb-3 flex items-center gap-2">
                      <Briefcase size={20} className="text-secondary-500" />
                      עסקים
                    </h4>
                    <div className="space-y-2">
                      {expandedFavoritesData.businesses.map((business, idx) => (
                        <div
                          key={idx}
                          className="p-3 sm:p-4 bg-surface-muted rounded-xl hover:bg-surface-muted transition-all cursor-pointer flex gap-3"
                          onClick={() => {
                            setSelectedFavoritesList(null);
                            navigate('business', `entity=${business.business_id}`);
                          }}
                        >
                          {business.cover_image ? (
                            <Thumbnail
                              src={business.cover_image}
                              alt={business.business_name}
                              className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-surface-sunken rounded-lg flex items-center justify-center">
                              <Briefcase size={24} className="text-ink-subtle" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h5 className="font-bold text-ink-strong text-sm sm:text-base">{business.business_name}</h5>
                            {business.short_description && (
                              <p className="text-xs sm:text-sm text-ink-muted mt-1 line-clamp-2">{business.short_description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {expandedFavoritesData.events?.length > 0 && (
                  <div>
                    <h4 className="text-lg font-bold text-ink-strong mb-3 flex items-center gap-2">
                      <Calendar size={20} className="text-info-600" />
                      מפגשים
                    </h4>
                    <div className="space-y-2">
                      {expandedFavoritesData.events.map((event, idx) => (
                        <div
                          key={idx}
                          className="p-3 sm:p-4 bg-info-50 rounded-xl hover:bg-info-100 transition-all cursor-pointer flex gap-3"
                          onClick={() => {
                            setSelectedFavoritesList(null);
                            navigate('business', `entity=${event.business_id}`);
                          }}
                        >
                          {event.cover_image ? (
                            <Thumbnail
                              src={event.cover_image}
                              alt={event.business_name}
                              className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-info-100 rounded-lg flex items-center justify-center">
                              <Calendar size={24} className="text-info-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h5 className="font-bold text-ink-strong text-sm sm:text-base">{event.business_name}</h5>
                            {event.short_description && (
                              <p className="text-xs sm:text-sm text-ink-muted mt-1 line-clamp-2">{event.short_description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {expandedFavoritesData.barters?.length > 0 && (
                  <div>
                    <h4 className="text-lg font-bold text-ink-strong mb-3 flex items-center gap-2">
                      <Handshake size={20} className="text-success-600" />
                      ברטרים
                    </h4>
                    <div className="space-y-2">
                      {expandedFavoritesData.barters.map((barter, idx) => (
                        <div
                          key={idx}
                          className="p-3 sm:p-4 bg-success-50 rounded-xl hover:bg-success-100 transition-all cursor-pointer flex gap-3"
                          onClick={() => {
                            setSelectedFavoritesList(null);
                            navigate('business', `entity=${barter.business_id}`);
                          }}
                        >
                          {barter.cover_image ? (
                            <Thumbnail
                              src={barter.cover_image}
                              alt={barter.business_name}
                              className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0"
                            />
                          ) : (
                            <div className="w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 bg-success-100 rounded-lg flex items-center justify-center">
                              <Handshake size={24} className="text-success-500" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <h5 className="font-bold text-ink-strong text-sm sm:text-base">{barter.business_name}</h5>
                            {barter.short_description && (
                              <p className="text-xs sm:text-sm text-ink-muted mt-1 line-clamp-2">{barter.short_description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
};

export default FavoritesView;
